import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "./utils";

// ✅ Add your new top-left logo path here
import MainLogo from "./assets/logooo.png";   // <-- change this to your file path

const Login = ({ setLoggedIn, setUsername }) => {
  const [username, setLocalUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => {
        setDeferredPrompt(null);
      });
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, [deferredPrompt]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const payload = { userName: username, password };

    try {
      setLoading(true);

      const response = await new Promise((resolve, reject) =>
        setTimeout(() => {
          if (username?.length > 0 && password?.length > 0) {
            resolve({ success: true });
          } else {
            reject(new Error("Invalid username or password"));
          }
        }, 2000)
      );

      if (response.success) {
        localStorage.setItem("username", username);
        setLoggedIn(true);
        setUsername(username);

        if (username === "admin") {
          navigate("/admin");
        } else {
          navigate("/userVerify");
        }
      }
    } catch (err) {
      setError(err.message || "Failed to login. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const disableLogin = useMemo(() => {
    return !(username?.length > 0 && password?.length > 0);
  }, [username, password]);

  const handleCancel = () => {
    setLocalUsername("");
    setPassword("");
    setError("");
    setLoading(false);
  };

  return (
  <Grid container sx={{ height: "100vh" }}>

    {/* Left Section */}
    <Grid
      item
      xs={12}
      md={6}
      sx={{
        backgroundColor: "#1e2e3e",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: { xs: 4, md: 0 },
        position: "relative",
      }}
    >

      {/* -------- TOP LOGO FIXED -------- */}
      <Box
        sx={{
          position: { xs: "relative", md: "absolute" },
          top: { xs: 0, md: 20 },
          left: { xs: 0, md: 20 },
          width: { xs: "220px", md: "270px" },
          marginBottom: { xs: 2, md: 0 },
        }}
      >
        <img
          src={MainLogo}
          alt="Main Logo"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />
      </Box>

      {/* Big Center Image */}
      <Card
        sx={{
          maxWidth: 300,
          borderRadius: 4,
          boxShadow: 3,
          backgroundColor: "#34495E",
          marginTop: { xs: 2, md: 10 },
        }}
      >
        <Logo width={"100%"} height={"100%"} />
      </Card>
    </Grid>

    {/* Right Section */}
    <Grid
      item
      xs={12}
      md={6}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 400, width: "100%" }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Welcome!
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Log in to your account
        </Typography>

        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <TextField
            label="Username (Email)"
            variant="outlined"
            fullWidth
            value={username}
            onChange={(e) => setLocalUsername(e.target.value)}
          />

          <TextField
            label="Password"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton onClick={togglePasswordVisibility}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={disableLogin || loading}
            sx={{
              backgroundColor: "#3F6955",
              "&:hover": { backgroundColor: "#2C5B44" },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Login"
            )}
          </Button>

          <Button type="button" variant="outlined" fullWidth onClick={handleCancel}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Grid>
  </Grid>
);

};

export default Login;
